import { UAParser } from 'ua-parser-js';

type DeviceType = UAParser.IDevice['type'] | 'desktop';

// Type declarations for Brave browser API
declare global {
    interface Navigator {
        brave?: {
            isBrave?: () => Promise<boolean>;
        };
    }
}

const userAgentParser = new UAParser();
const userAgent = userAgentParser.getResult();

export interface PlatformInfo {
    browserName?: string;
    browserVersion?: string;
    browserMajorVersion?: number;
    engineName?: string;
    engineVersion?: string;
    osName?: string;
    osVersion?: string;
    deviceType?: DeviceType;
    deviceModel?: string;
    deviceVendor?: string;
}
// Special cases:
export const isBrave = !!(navigator.brave && typeof navigator.brave.isBrave === 'function');
export const isAvastSecureBrowser = !!navigator.userAgentData?.brands?.some(
    (brand) => brand.brand === 'Avast Secure Browser',
);

const getPlatformInfo = (): PlatformInfo => {
    return {
        browserName: isBrave
            ? 'Brave'
            : isAvastSecureBrowser
              ? 'Avast Secure Browser'
              : userAgent.browser.name,
        // note that sometimes browser.version may be a non-parsable string e.g. "undefined".
        // Check ua-parser-js documentation for more details: https://github.com/faisalman/ua-parser-js/blob/master/test/data/ua/browser/browser-all.json
        browserVersion: userAgent.browser.version,
        browserMajorVersion: !userAgent.browser.major
            ? undefined
            : !isNaN(parseInt(userAgent.browser.major))
              ? parseInt(userAgent.browser.major)
              : undefined,
        engineName: userAgent.engine.name,
        engineVersion: userAgent.engine.version,
        osName: userAgent.os.name,
        osVersion: userAgent.os.version,
        deviceType: parseDeviceType(userAgent.device.type),
        deviceVendor: userAgent.device.vendor,
        deviceModel: userAgent.device.model,
    };
};

export const parseDeviceType = (deviceType: UAParser.IDevice['type']): DeviceType => {
    switch (deviceType) {
        case undefined: // Desktop is detected as undefined
            return 'desktop';
        default:
            return deviceType;
    }
};

export const platformInfo = getPlatformInfo();

// Platform:
export const isMobileDevice = platformInfo.deviceType === 'mobile';
export const isDesktopDevice = platformInfo.deviceType === 'desktop';

// Browser:
export const isSafari = ['Safari', 'Mobile Safari'].includes(platformInfo.browserName ?? '');
export const isChrome =
    ['Chrome', 'Mobile Chrome'].includes(platformInfo.browserName ?? '') &&
    !isBrave &&
    !isAvastSecureBrowser;
export const isEdge = ['Microsoft Edge', 'Edge'].includes(platformInfo.browserName ?? '');
export const isFirefox = ['Firefox', 'Mobile Firefox'].includes(platformInfo.browserName ?? '');

// Operation system:
export const isIOS = platformInfo.osName === 'iOS';
export const isAndroid = platformInfo.osName === 'Android';
